import { Box, Card, CircularProgress, Tabs, Tab, Badge } from "@mui/material";
import SearchInput from "components/SearchInput";
import FaiBox from "MuiComponents/FaiBox";
import DataList from "screens/Catalog/Inventory/component/DataList";
// import DataList from "screens/Catalog/Inventory/component/DataList";
import PageHeader from "screens/OnBoarding/components/PageHeader";
import dataTableData from "../data/dataTableData";
import { useEffect, useState } from "react";
import Axios from "services/api/config";
import {
	getApprovedMerchantList,
	getMerchantCounts,
	getMerchantList,
} from "services/api/routes/Admin";
import { GridPaginationModel } from "@mui/x-data-grid";

export default function MerchantOnboardingList({ setMerchant }) {
	const [merchantList, setMerchantList] = useState([]);
	const [type, setType] = useState("ALL");
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [rowCount, setRowCount] = useState(0);
	const [statusCount, setStatusCount] = useState({});
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});

	const fetchMerchantList = async (
		typeParam = type,
		searchParam = search,
		pageParam = paginationModel.page,
		pageSizeParam = paginationModel.pageSize
	) => {
		try {
			setLoading(true);
			// There's no single "all merchants" endpoint: /non-approved never
			// returns APPROVED merchants and /approved returns only those. For
			// the "All" tab, fetch BOTH and merge so approved merchants don't
			// vanish. Dedupe by id keeps it correct even if the backend later
			// starts including APPROVED in /non-approved too.
			if (typeParam === "ALL") {
				const [nonApproved, approved] = await Promise.all([
					Axios({
						...getMerchantList("ALL", 0, 100),
						params: { search: searchParam },
					}),
					Axios({
						...getApprovedMerchantList(0, 100),
						params: { search: searchParam },
					}),
				]);
				const seen = new Set();
				const all = [
					...(nonApproved.data?.data?.data || []),
					...(approved.data?.data?.data || []),
				].filter((m: any) => {
					const id = m.id || m._id;
					if (seen.has(id)) return false;
					seen.add(id);
					return true;
				});
				setRowCount(all.length);
				const start = pageParam * pageSizeParam;
				setMerchantList(all.slice(start, start + pageSizeParam));
			} else {
				const route =
					typeParam === "APPROVED"
						? getApprovedMerchantList(pageParam, pageSizeParam)
						: getMerchantList(typeParam, pageParam, pageSizeParam);
				const { data } = await Axios({
					...route,
					params: { search: searchParam },
				});
				setMerchantList(data.data?.data || []);
				setRowCount(data.data?.totalElements || 0);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const fetchMerchantCounts = async () => {
		try {
			const { data } = await Axios({ ...getMerchantCounts });
			setStatusCount(data.data || {});
		} catch (error) {
			console.error(error);
		} finally {
		}
	};

	useEffect(() => {
		fetchMerchantCounts();
		fetchMerchantList(
			type,
			search,
			paginationModel.page,
			paginationModel.pageSize
		);
		// eslint-disable-next-line
	}, [type, paginationModel.page, paginationModel.pageSize]);

	// Debounced search
	useEffect(() => {
		if (searchTimeout) clearTimeout(searchTimeout);
		const timeout = setTimeout(() => {
			if (search)
				fetchMerchantList(
					type,
					search,
					paginationModel.page,
					paginationModel.pageSize
				);
		}, 400);
		setSearchTimeout(timeout);
		return () => clearTimeout(timeout);
		// eslint-disable-next-line
	}, [search]);

	// Handle pagination change
	const handlePaginationModelChange = (newModel: GridPaginationModel) => {
		setPaginationModel(newModel);
	};

	const tabOptions = [
		{ label: "All", value: "ALL", color: "primary", countName: "all" },
		{
			label: "Approved",
			value: "APPROVED",
			color: "success",
			countName: "approved",
		},
		{
			label: "Completed",
			value: "ONBOARDING_COMPLETED",
			color: "success",
			countName: "onboardingCompleted",
		},
		{
			label: "In Progress",
			value: "PARTIAL_ONBOARDING",
			color: "warning",
			countName: "partialOnboarding",
		},
		// { label: "In Progress", value: "PARTIAL_ONBOARDING", color: "warning", countName: "pendingApproval" },
		{
			label: "Rejected",
			value: "REJECTED",
			color: "error",
			countName: "rejected",
		},
	];

	return (
		<Card className="my-6 p-3 md:p-6">
			<Box>
				<Box className="flex flex-wrap justify-between gap-4">
					<PageHeader title="Merchant Onboarding" />
					<Box className="flex gap-2">
						<SearchInput
							placeholder="Search"
							value={search}
							onChange={(e: any) => setSearch(e.target.value)}
						/>
					</Box>
				</Box>
				<Box className="my-4">
					<Tabs
						value={type}
						onChange={(_, v) => setType(v)}
						indicatorColor="primary"
						textColor="primary"
					>
						{tabOptions.map((tab) => (
							<Tab
								key={tab.value}
								label={
									<div className="flex items-center gap-5">
										<div>{tab.label}</div>
										<Badge
											color={tab.color as any}
											badgeContent={statusCount[tab.countName]}
											max={999}
											className="text-white mb-1"
											classes={{
												colorPrimary: "text-white",
											}}
										></Badge>
									</div>
								}
								value={tab.value}
							/>
						))}
					</Tabs>
				</Box>
				<FaiBox className={"inventory"}>
					<FaiBox className={"inventory-content"}>
						<FaiBox className={"inventory-content-body"}>
							{loading ? (
								<div className="flex justify-center">
									<CircularProgress />
								</div>
							) : (
								<DataList
									data={{
										...dataTableData,
										rowCount,
										rows: merchantList,
										paginationMode: "server",
										paginationModel,
										onPaginationModelChange: handlePaginationModelChange,
									}}
									onRowClick={setMerchant}
								/>
							)}
						</FaiBox>
					</FaiBox>
				</FaiBox>
			</Box>
		</Card>
	);
}
